import { memo, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Button from 'antd/es/button';
import Col from 'antd/es/col';
import Collapse from 'antd/es/collapse';
import ConfigProvider from 'antd/es/config-provider';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import Row from 'antd/es/row';
import Spin from 'antd/es/spin';
import Table from 'antd/es/table';
import {
  ArrowLeftOutlined,
  ClearOutlined,
  DownloadOutlined,
  InfoCircleFilled,
  InfoOutlined,
} from '@ant-design/icons';
import { useUnit } from 'effector-react';
import Modal from 'antd/es/modal/Modal';
import dayjs from 'dayjs';
import style from './Sectors.module.css';
import {
  $MatrixProjectConSheet,
  $MatrixProjectConSheetFilters,
  $MatrixProjectData,
  getMatrixProjectConSheetFx,
  getMatrixProjectDataFx,
  MatrixProjectGate,
  setMatrixProjectConSheetFiltersEv,
} from '../../models/sectorsModel/index.js';
import CircleGauge from '../../../components/UI/CircleGauge.jsx';
import DownloadFilteredButton from '../../components/FilterBlock/DownloadFilteredButton.jsx';

function SectorsViewProject() {
  const { id } = useParams();
  const data = useUnit($MatrixProjectData);

  const loading = useUnit(getMatrixProjectDataFx.pending);

  const navigate = useNavigate();

  const items = [
    {
      key: 'about',
      label: 'О проекте',
      children: <AboutSection description={data?.about} />,
      collapsible: !data ? 'disabled' : 'header',
    },
    {
      key: 'participants',
      label: 'Участники',
      children: <ParticipantsTable data={data} />,
      collapsible: !data ? 'disabled' : 'header',
    },
    {
      key: 'consolidated',
      label: 'Сводная ведомость',
      children: <ConsolSheet project={data?.name ?? undefined} />,
      collapsible: !data ? 'disabled' : 'header',
    },
  ];

  return (
    <>
      <MatrixProjectGate id={id} />
      <Spin spinning={loading}>
        <Col span={24} style={{ marginTop: 16 }}>
          <div className={'flex-8'}>
            <Button
              icon={<ArrowLeftOutlined />}
              className={style.back_button}
              onClick={() => navigate(-1)}
            />
            <div className={style.view_title}>{data?.name}</div>
          </div>
          <ConfigProvider
            theme={{
              components: {
                Collapse: {
                  headerBg: 'var(--primary-bg)',
                  contentPadding: 0,
                  borderlessContentPadding: 0,
                  borderlessContentBg: '#ffffff',
                  contentBg: '#ffffff',
                  headerPadding: 12,
                },
              },
            }}
          >
            <Collapse
              items={items}
              bordered={false}
              defaultActiveKey={['about', 'participants', 'consolidated']}
              style={{
                marginTop: 16,
                boxShadow: '0px 2px 8px 0px #00000026',
              }}
            />
          </ConfigProvider>
        </Col>
      </Spin>
    </>
  );
}

const AboutSection = memo(function ({ description }) {
  return (
    <div className={'flex-col-16'} style={{ background: '#fff' }}>
      <p style={{ paddingInline: 16 }}>{description}</p>
      {description && (
        <>
          <h3 className={style.otp_title}>Доля ОТП по проекту</h3>
          <div className={style.gauges_cnt}>
            <div className={style.gauge_card_left}>
              <CircleGauge
                size={150}
                strokeWidth={12}
                value={5}
                max={10}
                label={'В натуральном выражении'}
                percent
                mainColor={'var(--primary)'}
                bgColor={'var(--primary-bg)'}
                innerTextBold
                innerTextSize={24}
              />
            </div>
            <div className={style.gauge_card_right}>
              <CircleGauge
                size={150}
                strokeWidth={12}
                value={4}
                max={10}
                label={'В стоимостном выражении'}
                percent
                mainColor={'var(--purple)'}
                bgColor={'var(--purple-light)'}
                innerTextBold
                innerTextSize={24}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
});

const ParticipantsTable = memo(function ({ data }) {
  const dataSource = [
    {
      id: 1,
      customer: data?.customer ?? '-',
      general_contractor: data?.general_contractor ?? '-',
      project_organization: data?.project_organization ?? '-',
    },
  ];

  function renderItem(text) {
    return (
      <div className={'flex-between'}>
        <span>{text}</span>
        <InfoCircleFilled
          style={{ cursor: 'pointer', color: 'var(--primary)' }}
        />
      </div>
    );
  }

  const columns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: '2%',
    },
    {
      title: 'Заказчик',
      dataIndex: 'customer',
      key: 'customer',
      width: '32%',
      render: (text) => renderItem(text),
    },
    {
      title: 'Ген. Подрядчик',
      dataIndex: 'general_contractor',
      key: 'general_contractor',
      width: '32%',
      render: (text) => renderItem(text),
    },
    {
      title: 'Проектная организация',
      dataIndex: 'project_organization',
      key: 'project_organization',
      width: '32%',
      render: (text) => renderItem(text),
    },
  ];

  return (
    <div style={{ background: '#fff', paddingInline: 12, paddingBlock: 20 }}>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        style={{
          border: '1px solid #0000000F',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      />
    </div>
  );
});

const ConsolSheet = memo(function ({ project }) {
  const { data } = useUnit($MatrixProjectConSheet);
  const filtersState = useUnit($MatrixProjectConSheetFilters);
  const setFilters = useUnit(setMatrixProjectConSheetFiltersEv);

  const sheetLoading = useUnit(getMatrixProjectConSheetFx.pending);

  const [modal, setModal] = useState({ open: false, data: [] });

  const dataSource = useMemo(() => {
    return data?.map((i, idx) => ({ ...i, id: idx + 1 }));
  }, [data]);

  const [filtersForm] = Form.useForm();

  const prepareValues = (values) => {
    for (const [key, value] of Object.entries(values)) {
      if (key.includes('year_of_purchase')) {
        values[key] = parseInt(dayjs(value).format('YYYY'));
      }
      if (!value && value !== false) {
        delete values[key];
      }
    }
    return values;
  };

  const prepareDownload = () => {
    const payload = prepareValues(filtersForm.getFieldsValue());
    console.log(payload);
    // download(payload);
  };

  const onReset = () => {
    filtersForm.resetFields();
    setFilters({
      ...Object.fromEntries(
        Object.entries(filtersState).map(([key, value]) => [key, null])
      ),
      project,
    });
  };

  const columns = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Наименование товарной позиции, работы/услуги',
      dataIndex: 'name',
      width: '40%',
      key: 'name',
    },
    {
      title: 'ТНВЭД',
      dataIndex: 'tnved',
      align: 'center',
      key: 'tnved',
    },
    {
      title: 'Ед. измерения',
      dataIndex: 'unit',
      key: 'unit',
    },
    {
      title: 'Потребность',
      dataIndex: 'quantity',
      key: 'quantity',
    },
    {
      title: 'Цена за единицу, с НДС',
      dataIndex: 'unit_price',
      key: 'unit_price',
      render: (price) =>
        Number.isFinite(price)
          ? new Intl.NumberFormat('ru-KZ', { notation: 'standard' }).format(
              price
            )
          : '-',
    },
    {
      title: 'Количество ОТП',
      dataIndex: 'manufacturers',
      key: 'manufacturers',
      render: (m) => (
        <div className="flex-col-8">
          <div
            className={style.otp_count}
            style={m?.length > 0 ? { cursor: 'pointer' } : null}
            onClick={() =>
              m && m?.length > 0 ? setModal({ open: true, data: m }) : undefined
            }
          >
            {m?.length}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div style={{ background: '#fff', paddingInline: 12, paddingBlock: 20 }}>
      <OTPModal
        state={modal}
        onClose={() => setModal({ open: false, data: [] })}
      />
      <Row
        justify={'start'}
        align={'middle'}
        style={{ gap: 24, width: '100%', flexWrap: 'nowrap' }}
      >
        <Form
          form={filtersForm}
          layout="vertical"
          onFinish={(v) => setFilters(prepareValues(v))}
          style={{ flex: 1 }}
        >
          <Row style={{ gap: 16, flexWrap: 'nowrap' }}>
            <Form.Item
              name={'name'}
              label={'Наименование товарной позиции'}
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
            <Form.Item name={'tnved'} label={'ТНВЭД'} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item name={'unit_price'} label={'Цена'} style={{ flex: 1 }}>
              <Input />
            </Form.Item>
            <Form.Item
              name={'otp_count'}
              label={'Количество ОТП'}
              style={{ flex: 1 }}
            >
              <Input />
            </Form.Item>
          </Row>
        </Form>
        <Row style={{ gap: 4, marginTop: 4, flexShrink: 0 }}>
          <Button type="primary" onClick={() => filtersForm.submit()}>
            Применить фильтр
          </Button>
          <Button
            onClick={onReset}
            type={'primary'}
            ghost
            icon={<ClearOutlined />}
          />
          <DownloadFilteredButton onDownload={prepareDownload} />
        </Row>
      </Row>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        loading={sheetLoading}
      />
    </div>
  );
});

const OTPModal = memo(function ({ state, onClose }) {
  const { open, data } = state;
  const columns = [
    { title: 'Предприятие', dataIndex: 'name', key: 'name' },
    { title: 'БИН', dataIndex: 'gov_id', key: 'gov_id' },
    {
      dataIndex: 'id',
      key: 'id',
      render: (id, record) => (
        <Link to={`/view-organization/${record.gov_id}`} target={'_blank'}>
          <Button size={'small'} type={'primary'} icon={<InfoOutlined />} />
        </Link>
      ),
    },
  ];
  return (
    <Modal
      title={
        <div className={'flex-between'}>
          <h3 style={{ marginBlock: 0 }}>{'Дополнительные данные'}</h3>
          <DownloadOutlined />
        </div>
      }
      open={open}
      closeIcon={null}
      width={'60dvw'}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Table dataSource={data} columns={columns} pagination={false} />
      <Button
        type={'primary'}
        ghost
        onClick={onClose}
        style={{ marginTop: 20 }}
      >
        Закрыть
      </Button>
    </Modal>
  );
});
export default SectorsViewProject;
